import { queryOptions, type QueryClient } from "@tanstack/react-query"
import type { Grantee, Level } from "@beacon/core/grants"
import type {
  ActivityEntry,
  AreaColour,
  AreaSummary,
  Attachment,
  AttachmentUpload,
  Board,
  ChannelRule,
  ChannelRuleEvents,
  Comment,
  Epic,
  EpicsTab,
  EpicView,
  Grantable,
  Hue,
  LabelRemoval,
  LabelSummary,
  MyTickets,
  NewAttachment,
  NewEpic,
  NewProject,
  NewTicket,
  Priority,
  Project,
  ProjectChange,
  ProjectGrant,
  ProjectMember,
  Relation,
  SearchHit,
  Size,
  SlackChannel,
  StatusCategory,
  StatusSummary,
  Ticket,
  TicketType,
  TicketView,
} from "@beacon/core/projects"
import { call } from "@/administration/client.ts"
import { apiBase } from "@/sign-in/client.ts"
import { withBoardLabels, type BoardFilter } from "./board.ts"

const at = (key: string) => `/projects/${encodeURIComponent(key)}`

export const listProjects = ({ archived }: { archived: boolean }) =>
  call<Project[]>("GET", `/projects${archived ? "?archived=true" : ""}`)

export const createProject = (project: NewProject) => call<Project>("POST", "/projects", project)

export const fetchProject = (key: string) => call<Project>("GET", at(key))

/** The signed-in Member's Personal Project, which the web API makes the first time it is asked. */
export const fetchPersonalProject = () => call<Project>("PUT", "/my-project")

export const updateProject = (
  key: string,
  details: { name?: string; description?: string; insightsEnabled?: boolean },
) => call<Project>("PATCH", at(key), details)

export const archiveProject = (key: string) => call<Project>("POST", `${at(key)}/archive`)

export const unarchiveProject = (key: string) => call<Project>("POST", `${at(key)}/unarchive`)

export const listGrants = (key: string) => call<ProjectGrant[]>("GET", `${at(key)}/grants`)

export const fetchGrantable = (key: string) => call<Grantable>("GET", `${at(key)}/grantable`)

export const grant = (key: string, grantee: Grantee, level: Level) =>
  call<void>("PUT", `${at(key)}/grants`, { grantee, level })

export const revoke = (key: string, grantee: Grantee) =>
  call<void>("DELETE", `${at(key)}/grants/${encodeURIComponent(grantee)}`)

/**
 * What a Board is asked for: older closed cards or not, the filters its URL names, the Labels it
 * names, by id, and whether completed Epics and their cards are left out.
 */
export type BoardView = {
  older: boolean
  filters: Partial<Record<BoardFilter, string>>
  labels: readonly string[]
  withoutCompletedEpics?: boolean
}

export const fetchBoard = (
  key: string,
  { older, filters, labels, withoutCompletedEpics }: BoardView,
) => {
  const params = new URLSearchParams({
    ...(older && { older: "true" }),
    ...filters,
    ...(withoutCompletedEpics && { completedEpics: "false" }),
  })
  const query = withBoardLabels(params, labels).toString()
  return call<Board>("GET", `${at(key)}/board${query ? `?${query}` : ""}`)
}

export const fetchStatuses = (key: string) => call<StatusSummary[]>("GET", `${at(key)}/statuses`)

export const addStatus = (key: string, name: string, category: StatusCategory) =>
  call<StatusSummary[]>("POST", `${at(key)}/statuses`, { name, category })

export const renameStatus = (key: string, statusId: string, name: string) =>
  call<StatusSummary[]>("PUT", `${at(key)}/statuses/${statusId}/name`, { name })

export const setStatusCategory = (key: string, statusId: string, category: StatusCategory) =>
  call<StatusSummary[]>("PUT", `${at(key)}/statuses/${statusId}/category`, { category })

/** Puts every one of a Project's Statuses in the order given, from the left. */
export const reorderStatuses = (key: string, order: readonly string[]) =>
  call<StatusSummary[]>("PUT", `${at(key)}/statuses`, { order })

/** Removes a Status, moving any tickets it holds to `moveTo`. */
export const removeStatus = (key: string, statusId: string, moveTo?: string) =>
  call<StatusSummary[]>(
    "DELETE",
    `${at(key)}/statuses/${statusId}${moveTo ? `?moveTo=${encodeURIComponent(moveTo)}` : ""}`,
  )

export const fetchMembers = (key: string) => call<ProjectMember[]>("GET", `${at(key)}/members`)

export const fetchMyTickets = ({
  closed,
  archived,
  size,
}: {
  closed: boolean
  archived: boolean
  /** One Size, or `none` for the unsized: only those tickets. */
  size?: string
}) => {
  const query = new URLSearchParams({
    ...(closed && { closed: "true" }),
    ...(archived && { archived: "true" }),
    ...(size && { size }),
  }).toString()
  return call<MyTickets[]>("GET", `/my-tickets${query ? `?${query}` : ""}`)
}

/**
 * Tickets and Epics whose words match `text`, across every Project the signed-in Member can read
 * that isn't archived: the best twenty, best first.
 */
export const searchTickets = (text: string) =>
  call<SearchHit[]>("GET", `/tickets/search?${new URLSearchParams({ text }).toString()}`)

export const createTicket = (key: string, ticket: NewTicket) =>
  call<Ticket>("POST", `${at(key)}/tickets`, ticket)

export const fetchTicket = (key: string, number: number) =>
  call<TicketView | EpicView>("GET", `${at(key)}/tickets/${number}`)

/** Moves a Personal Project's ticket to the team Project `project` keys, answering it there. */
export const moveToProject = (key: string, number: number, project: string) =>
  call<Ticket>("POST", `${at(key)}/tickets/${number}/move`, { project })

export const createEpic = (key: string, epic: NewEpic) =>
  call<Epic>("POST", `${at(key)}/epics`, epic)

export const fetchEpicsTab = (key: string) => call<EpicsTab>("GET", `${at(key)}/epics`)

/** Moves an Epic in its Project's Epic order: just below the Epic `above`, or to the top. */
export const moveEpic = (key: string, number: number, above: number | null) =>
  call<Epic>("PUT", `${at(key)}/epics/${number}/order`, { above })

export const fetchAreas = (key: string) => call<AreaSummary[]>("GET", `${at(key)}/areas`)

/** Adds an Area, last; without a colour it takes the hue fewest of the Project's Areas use. */
export const addArea = (key: string, name: string, colour?: AreaColour) =>
  call<AreaSummary[]>("POST", `${at(key)}/areas`, { name, ...(colour && { colour }) })

export const renameArea = (key: string, areaId: string, name: string) =>
  call<AreaSummary[]>("PUT", `${at(key)}/areas/${areaId}/name`, { name })

export const recolourArea = (key: string, areaId: string, colour: AreaColour) =>
  call<AreaSummary[]>("PUT", `${at(key)}/areas/${areaId}/colour`, { colour })

/** Puts every one of a Project's Areas in the order given. */
export const reorderAreas = (key: string, order: readonly string[]) =>
  call<AreaSummary[]>("PUT", `${at(key)}/areas`, { order })

/** Removes an Area, moving the tickets and Epics in it to `moveTo`, or to no Area without one. */
export const removeArea = (key: string, areaId: string, moveTo?: string) =>
  call<AreaSummary[]>(
    "DELETE",
    `${at(key)}/areas/${areaId}${moveTo ? `?moveTo=${encodeURIComponent(moveTo)}` : ""}`,
  )

export const fetchLabels = (key: string) => call<LabelSummary[]>("GET", `${at(key)}/labels`)

/** Adds a Label, last; without a hue it takes the one fewest of the Project's Labels use. */
export const addLabel = (key: string, name: string, hue?: Hue) =>
  call<LabelSummary[]>("POST", `${at(key)}/labels`, { name, ...(hue && { hue }) })

export const renameLabel = (key: string, labelId: string, name: string) =>
  call<LabelSummary[]>("PUT", `${at(key)}/labels/${labelId}/name`, { name })

export const recolourLabel = (key: string, labelId: string, hue: Hue) =>
  call<LabelSummary[]>("PUT", `${at(key)}/labels/${labelId}/hue`, { hue })

/** Puts every one of a Project's Labels in the order given. */
export const reorderLabels = (key: string, order: readonly string[]) =>
  call<LabelSummary[]>("PUT", `${at(key)}/labels`, { order })

/** Deletes a Label, taking it off every ticket and Epic that carries it. */
export const removeLabel = (key: string, labelId: string) =>
  call<LabelRemoval>("DELETE", `${at(key)}/labels/${labelId}`)

/** Moves a ticket to a Status: to the top of it, or, dragged, just below the card `above`. */
export const moveTicket = (key: string, number: number, statusId: string, above?: number | null) =>
  call<Ticket>("PUT", `${at(key)}/tickets/${number}/status`, {
    statusId,
    ...(above !== undefined && { above }),
  })

const ticketAt = (key: string, number: number) => `${at(key)}/tickets/${number}`

export const editTitle = (key: string, number: number, title: string, basedOn: number) =>
  call<Ticket | Epic>("PUT", `${ticketAt(key, number)}/title`, { title, basedOn })

export const editDescription = (
  key: string,
  number: number,
  description: string,
  basedOn: number,
) => call<Ticket | Epic>("PUT", `${ticketAt(key, number)}/description`, { description, basedOn })

export const setPriority = (key: string, number: number, priority: Priority) =>
  call<Ticket>("PUT", `${ticketAt(key, number)}/priority`, { priority })

/** Sets a ticket's Size, or clears it with none. */
export const setSize = (key: string, number: number, size: Size | null) =>
  call<Ticket>("PUT", `${ticketAt(key, number)}/size`, { size })

export const setType = (key: string, number: number, type: TicketType) =>
  call<Ticket>("PUT", `${ticketAt(key, number)}/type`, { type })

export const setEpic = (key: string, number: number, epic: number | null) =>
  call<Ticket>("PUT", `${ticketAt(key, number)}/epic`, { epic })

/** Puts a ticket or an Epic in an Area, or in none; an Epic's tickets go with it. */
export const setArea = (key: string, number: number, areaId: string | null) =>
  call<Ticket | Epic>("PUT", `${ticketAt(key, number)}/area`, { areaId })

/** Puts one of its Project's Labels on a ticket or an Epic, beside those it carries. */
export const labelTicket = (key: string, number: number, labelId: string) =>
  call<Ticket | Epic>("PUT", `${ticketAt(key, number)}/labels/${encodeURIComponent(labelId)}`)

/** Takes one Label off a ticket or an Epic. */
export const unlabelTicket = (key: string, number: number, labelId: string) =>
  call<Ticket | Epic>("DELETE", `${ticketAt(key, number)}/labels/${encodeURIComponent(labelId)}`)

export const relate = (key: string, number: number, relation: Relation, target: string) =>
  call<Ticket>("PUT", `${ticketAt(key, number)}/relations`, { relation, target })

export const unrelate = (key: string, number: number, relation: Relation, target: string) =>
  call<Ticket>(
    "DELETE",
    `${ticketAt(key, number)}/relations/${relation}/${encodeURIComponent(target)}`,
  )

export const comment = (
  key: string,
  number: number,
  body: string,
  attachmentIds: readonly string[] = [],
) => call<Comment>("POST", `${ticketAt(key, number)}/comments`, { body, attachmentIds })

export const editComment = (
  key: string,
  number: number,
  commentId: string,
  body: string,
  basedOn: number,
) => call<Comment>("PUT", `${ticketAt(key, number)}/comments/${commentId}`, { body, basedOn })

export const deleteComment = (key: string, number: number, commentId: string) =>
  call<void>("DELETE", `${ticketAt(key, number)}/comments/${commentId}`)

export const assign = (key: string, number: number, memberId: string) =>
  call<Ticket>("PUT", `${ticketAt(key, number)}/assignees/${encodeURIComponent(memberId)}`)

export const unassign = (key: string, number: number, memberId: string) =>
  call<Ticket>("DELETE", `${ticketAt(key, number)}/assignees/${encodeURIComponent(memberId)}`)

export const deleteTicket = (key: string, number: number) =>
  call<void>("DELETE", ticketAt(key, number))

export const attachFile = (key: string, number: number, file: NewAttachment) =>
  call<AttachmentUpload>("POST", `${ticketAt(key, number)}/attachments`, file)

export const confirmAttachment = (key: string, number: number, attachmentId: string) =>
  call<Attachment>("POST", `${ticketAt(key, number)}/attachments/${attachmentId}/confirm`)

export const removeAttachment = (key: string, number: number, attachmentId: string) =>
  call<void>("DELETE", `${ticketAt(key, number)}/attachments/${attachmentId}`)

/**
 * Where the browser fetches an attachment's bytes: the web API, which checks the session and
 * redirects to the file, so it serves as an image's `src` and a download's `href` alike.
 */
export const attachmentHref = (key: string, number: number, attachmentId: string) =>
  `${apiBase}${ticketAt(key, number)}/attachments/${attachmentId}`

export const listChannelRules = (key: string) =>
  call<ChannelRule[]>("GET", `${at(key)}/channel-rules`)

export const listChannelChoices = (key: string) =>
  call<SlackChannel[]>("GET", `${at(key)}/channel-choices`)

export const addChannelRule = (key: string, channel: string, events: ChannelRuleEvents) =>
  call<ChannelRule>("POST", `${at(key)}/channel-rules`, { channel, events })

export const editChannelRule = (
  key: string,
  ruleId: string,
  channel: string,
  events: ChannelRuleEvents,
) => call<ChannelRule>("PUT", `${at(key)}/channel-rules/${ruleId}`, { channel, events })

export const removeChannelRule = (key: string, ruleId: string) =>
  call<void>("DELETE", `${at(key)}/channel-rules/${ruleId}`)

export const fetchProjectActivity = (key: string) =>
  call<ActivityEntry<ProjectChange>[]>("GET", `${at(key)}/activity`)

/** How often a Board or a ticket's page asks again while it is open, for other people's changes. */
const refreshEvery = 15_000

/**
 * Where Projects keeps what the web API answered. Everything about one Project sits under its key,
 * so a change to it refreshes all of it at once: its Board, its tickets, its grants, its Activity.
 */
export const projectKeys = {
  all: ["projects"] as const,
  of: (key: string) => ["projects", key] as const,
  boards: (key: string) => ["projects", key, "board"] as const,
}

/**
 * Every Project the signed-in Member can read, archived ones too when asked. `list` is lower case,
 * so no Project's key meets it.
 */
export const projectsQuery = (archived: boolean) =>
  queryOptions({
    queryKey: [...projectKeys.all, "list", { archived }],
    queryFn: () => listProjects({ archived }),
  })

export const projectQuery = (key: string) =>
  queryOptions({ queryKey: [...projectKeys.of(key), "details"], queryFn: () => fetchProject(key) })

export const boardQuery = (key: string, view: BoardView) =>
  queryOptions({
    queryKey: [...projectKeys.boards(key), view],
    queryFn: () => fetchBoard(key, view),
    refetchInterval: refreshEvery,
  })

export const statusesQuery = (key: string) =>
  queryOptions({
    queryKey: [...projectKeys.of(key), "statuses"],
    queryFn: () => fetchStatuses(key),
  })

export const areasQuery = (key: string) =>
  queryOptions({ queryKey: [...projectKeys.of(key), "areas"], queryFn: () => fetchAreas(key) })

export const labelsQuery = (key: string) =>
  queryOptions({ queryKey: [...projectKeys.of(key), "labels"], queryFn: () => fetchLabels(key) })

/** Everyone a Project's grants reach: whom its tickets may be assigned to and filtered by. */
export const membersQuery = (key: string) =>
  queryOptions({ queryKey: [...projectKeys.of(key), "members"], queryFn: () => fetchMembers(key) })

/** Where My tickets is kept. A change to any ticket may change it, so each one refreshes it. */
export const myTicketsKey = ["my-tickets"] as const

export const myTicketsQuery = (closed: boolean, archived: boolean, size?: string) =>
  queryOptions({
    queryKey: [...myTicketsKey, { closed, archived, size }],
    queryFn: () => fetchMyTickets({ closed, archived, ...(size && { size }) }),
    refetchInterval: refreshEvery,
  })

/**
 * The command palette's ticket search, for words the Member has stopped typing. Kept for a moment,
 * so typing back to a search already made shows its hits at once; `search` is lower case, so no
 * Project's key meets it.
 */
export const ticketSearchQuery = (text: string) =>
  queryOptions({
    queryKey: [...projectKeys.all, "search", text],
    queryFn: () => searchTickets(text),
    staleTime: 30_000,
  })

/** A Project's Epics tab: its Epics in the Epic order and their tickets. */
export const epicsTabQuery = (key: string) =>
  queryOptions({
    queryKey: [...projectKeys.of(key), "epics"],
    queryFn: () => fetchEpicsTab(key),
    refetchInterval: refreshEvery,
  })

export const ticketQuery = (key: string, number: number) =>
  queryOptions({
    queryKey: [...projectKeys.of(key), "tickets", number],
    queryFn: () => fetchTicket(key, number),
    refetchInterval: refreshEvery,
  })

export const grantsQuery = (key: string) =>
  queryOptions({ queryKey: [...projectKeys.of(key), "grants"], queryFn: () => listGrants(key) })

export const grantableQuery = (key: string) =>
  queryOptions({
    queryKey: [...projectKeys.of(key), "grantable"],
    queryFn: () => fetchGrantable(key),
  })

export const channelRulesQuery = (key: string) =>
  queryOptions({
    queryKey: [...projectKeys.of(key), "channel-rules"],
    queryFn: () => listChannelRules(key),
  })

/** The Slack channels Beacon is in, which only a Member with Write is shown. */
export const channelChoicesQuery = (key: string) =>
  queryOptions({
    queryKey: [...projectKeys.of(key), "channel-choices"],
    queryFn: () => listChannelChoices(key),
  })

export const projectActivityQuery = (key: string) =>
  queryOptions({
    queryKey: [...projectKeys.of(key), "activity"],
    queryFn: () => fetchProjectActivity(key),
  })

/**
 * Asks again for everything a change to one of a Project's tickets may have changed: the Project's
 * Boards and pages, and My tickets.
 */
export function refreshTicket(queryClient: QueryClient, key: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: projectKeys.of(key) }),
    queryClient.invalidateQueries({ queryKey: myTicketsKey }),
  ])
}
